import { useAuthStore } from "./auth";
import { fetchAuthSession } from "aws-amplify/auth";

// In development, Vite proxy rewrites /api to localhost:8080
// In production, we call the API directly
const API_BASE = import.meta.env.PROD
  ? (import.meta.env.VITE_API_URL || "https://api-asm.sertua.com/v1/admin")
  : "/api/v1/admin";

// Try to refresh the Cognito token via Amplify
async function tryRefreshToken(): Promise<boolean> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const newToken = session.tokens?.idToken?.toString();
    if (newToken) {
      useAuthStore.setState({ token: newToken });
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = useAuthStore.getState().token;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Handle 401 - try to refresh token
    if (response.status === 401) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        const newToken = useAuthStore.getState().token;
        const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
          ...options,
          headers: {
            ...headers,
            ...(newToken && { Authorization: `Bearer ${newToken}` }),
          },
        });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            return {} as T;
          }
          return retryResponse.json() as Promise<T>;
        }
      }
      // Refresh failed - redirect to login
      useAuthStore.getState().logout();
      window.location.href = "/login";
      throw new Error("Session expired. Please log in again.");
    }

    // Try to parse error response
    let errorMessage = "API request failed";
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // Response might not be JSON
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  // Handle empty responses (204 No Content, etc.)
  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

// Admin Profile
export interface AdminProfile {
  id: string;
  email: string;
  name?: string;
  role: string;
  created_at: string;
  last_login_at?: string;
}

// Organizations
export interface Organization {
  id: string;
  name: string;
  slug: string;
  tier: string;
  created_at: string;
  updated_at: string;
}

// Organization Settings
export interface OrganizationSettings {
  skip_domain_verification: boolean;
}

export interface PaginatedOrganizations {
  organizations: Organization[];
  total: number;
  page: number;
  per_page: number;
}

// Users
export interface User {
  id: string;
  email: string;
  name?: string;
  role: string;
  org_id: string;
  org_name: string;
  created_at: string;
}

export interface PaginatedUsers {
  users: User[];
  total: number;
  page: number;
  per_page: number;
}

// Invitations
export interface Invitation {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
}

// System Statistics
export interface SystemStats {
  total_organizations: number;
  total_users: number;
  total_scans: number;
  total_admins: number;
  scans_last_24h: number;
  scans_last_7d: number;
  scans_last_30d: number;
}

// Tier Configuration
export interface TierConfig {
  tier: string;
  rate_limit_rpm: number;
  max_concurrent_scans: number;
  max_domains?: number;
  monthly_scan_quota?: number;
  allows_self_assessment: boolean;
  min_interval_minutes: number;
  max_schedules?: number;
  allowed_frequencies: string[];
  supports_custom_cron: boolean;
  asset_inventory_enabled: boolean;
  webhooks_enabled: boolean;
  max_webhooks?: number;
}

// Organization Overrides
export interface OrgOverrides {
  org_id: string;
  rate_limit_rpm?: number;
  max_concurrent_scans?: number;
  max_domains?: number;
  monthly_scan_quota?: number;
  min_interval_minutes?: number;
  max_schedules?: number;
  allowed_frequencies?: string[];
  supports_custom_cron?: boolean;
  asset_inventory_enabled?: boolean;
  webhooks_enabled?: boolean;
  max_webhooks?: number;
  reason?: string;
  expires_at?: string;
}

// Recon Types
export interface ReconStats {
  total_domains: number;
  scanned_domains: number;
  pending_domains: number;
  scanned_last_24h: number;
  scanned_last_7d: number;
  unique_tlds: number;
  unique_countries: number;
  total_results: number;
  avg_email_security_score: number;
  avg_security_headers_score: number;
  scanned_this_hour: number;
  max_per_hour: number;
  is_rate_limited: boolean;
  enabled: boolean;
}

export interface ReconConfig {
  enabled: boolean;
  modules: string[];
  max_domains_per_hour: number;
  target_tlds: string[];
  target_countries: string[];
  min_idle_workers: number;
  scan_interval_hours: number;
  rate_limit_dns_qps: number;
  rate_limit_http_qps: number;
  rate_limit_whois_qps: number;
  rate_limit_github_qps: number;
  rate_limit_external_api_qps: number;
  retention_days: number;
  campaign_only: boolean;
}

export interface ReconDomain {
  id: string;
  domain: string;
  tld?: string;
  country_code?: string;
  source: string;
  last_scanned_at?: string;
  scan_count: number;
  has_dns: boolean;
  has_email_security: boolean;
  has_tls: boolean;
  has_whois: boolean;
  has_headers: boolean;
  has_cloud: boolean;
  has_asn: boolean;
  has_tech: boolean;
  has_repos: boolean;
  has_crawl_hints: boolean;
  has_js_analysis: boolean;
  has_favicon: boolean;
  has_typosquat: boolean;
  has_ipintel: boolean;
  has_vuln: boolean;
  priority: number;
  discovered_at: string;
}

export interface PaginatedReconDomains {
  domains: ReconDomain[];
  total: number;
  page: number;
  per_page: number;
}

export interface ReconFinding {
  result_id: string;
  domain_id: string;
  domain: string;
  finding_type: string;
  severity: "low" | "medium" | "high" | "critical";
  title: string;
  description: string;
  module: string;
  found_at: string;
}

export interface ReconResult {
  id: string;
  domain_id: string;
  modules: string[];
  partial: boolean;
  subdomains_found: number;
  certificates_found: number;
  email_security_score?: number;
  security_headers_score?: number;
  cloud_assets_found: number;
  technologies_found: number;
  repos_found: number;
  crawl_hints_found: number;
  js_findings_found: number;
  favicon_hashes_found: number;
  typosquats_found: number;
  ip_intel_count: number;
  vulns_found: number;
  findings: ReconResultFinding[];
  started_at: string;
  completed_at?: string;
}

export interface ReconResultFinding {
  finding_type: string;
  severity: string;
  title: string;
  description: string;
  module: string;
}

export interface ReconImportRequest {
  source: "tranco" | "majestic" | "umbrella" | "custom" | "csv_upload";
  url?: string;
  limit?: number;
  target_tlds?: string[];
  csv_content?: string;
}

export interface ReconImportResponse {
  imported: number;
  source: string;
  message: string;
}

// Campaign Types
export interface ReconCampaign {
  id: string;
  name: string;
  description?: string;
  sector: string;
  country_code: string;
  modules?: string[];
  scan_interval_hours?: number;
  max_domains_per_hour?: number;
  priority: number;
  status: "active" | "paused" | "completed" | "archived";
  domain_count: number;
  scanned_count: number;
  last_scan_at?: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedCampaigns {
  campaigns: ReconCampaign[];
  total: number;
  page: number;
  per_page: number;
}

export interface CampaignStats {
  domain_count: number;
  scanned_count: number;
  avg_email_score?: number;
  avg_headers_score?: number;
  total_findings: number;
  total_vulns: number;
  total_technologies: number;
}

export interface CampaignFinding {
  result_id: string;
  domain_id: string;
  domain: string;
  finding_type?: string;
  severity?: string;
  title?: string;
  description?: string;
  module?: string;
  found_at?: string;
}

export interface CampaignResult {
  id: string;
  domain_id: string;
  domain: string;
  modules: string[];
  partial: boolean;
  email_security_score?: number;
  security_headers_score?: number;
  technologies_found: number;
  vulns_found: number;
  started_at: string;
  completed_at?: string;
}

export interface CreateCampaignRequest {
  name: string;
  description?: string;
  sector: string;
  country_code: string;
  modules?: string[];
  scan_interval_hours?: number;
  max_domains_per_hour?: number;
  priority?: number;
}

export interface UpdateCampaignRequest {
  name?: string;
  description?: string;
  sector?: string;
  country_code?: string;
  modules?: string[];
  scan_interval_hours?: number;
  max_domains_per_hour?: number;
  priority?: number;
  status?: string;
}

// Analytics Types (Tier 1 - Materialized Views)
export interface TldSummary {
  tld: string | null;
  domain_count: number;
  scanned_count: number;
  avg_email_score: number;
  avg_headers_score: number;
  total_technologies: number;
  total_vulns: number;
  total_typosquats: number;
  total_subdomains: number;
  total_cloud_assets: number;
}

export interface FindingDistribution {
  module: string | null;
  finding_type: string | null;
  severity: string | null;
  count: number;
}

export interface DailyActivity {
  scan_date: string | null;
  scans: number;
  partial_scans: number;
  avg_email_score: number;
  avg_headers_score: number;
  subdomains: number;
  vulns: number;
  technologies: number;
}

// Analytics Types (Tier 2 - MapReduce Jobs)
export type AnalyticsJobType =
  | "tech_distribution"
  | "cloud_analysis"
  | "security_scores"
  | "vuln_prevalence"
  | "subdomain_patterns";

export interface AnalyticsJob {
  id: string;
  job_type: string;
  date_start: string;
  date_end: string;
  status: "running" | "completed" | "failed";
  progress?: Record<string, unknown>;
  result?: Record<string, unknown>;
  created_at: string;
  completed_at?: string;
  error?: string;
}

// Admin API
export const adminApi = {
  // Profile
  getProfile: () => fetchWithAuth<AdminProfile>("/me"),
  listAdmins: () => fetchWithAuth<AdminProfile[]>("/admins"),

  // Organizations
  listOrganizations: async (params?: { page?: number; per_page?: number }): Promise<PaginatedOrganizations> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<PaginatedOrganizations>(`/organizations?${searchParams}`);
  },
  getOrganization: (id: string) => fetchWithAuth<Organization>(`/organizations/${id}`),
  createOrganization: (req: { name: string; slug?: string; tier?: string }) =>
    fetchWithAuth<Organization>(`/organizations`, {
      method: "POST",
      body: JSON.stringify(req),
    }),
  updateOrganization: (id: string, req: { name?: string; tier?: string }) =>
    fetchWithAuth<Organization>(`/organizations/${id}`, {
      method: "PUT",
      body: JSON.stringify(req),
    }),
  updateOrganizationTier: (id: string, tier: string) =>
    fetchWithAuth<Organization>(`/organizations/${id}`, {
      method: "PUT",
      body: JSON.stringify({ tier }),
    }),
  getOrganizationSettings: (id: string) =>
    fetchWithAuth<OrganizationSettings>(`/organizations/${id}/settings`),
  updateOrganizationSettings: (id: string, settings: Partial<OrganizationSettings>) =>
    fetchWithAuth<OrganizationSettings>(`/organizations/${id}/settings`, {
      method: "PATCH",
      body: JSON.stringify(settings),
    }),

  // Organization Invitations
  inviteToOrg: (orgId: string, req: { email: string; role?: string }) =>
    fetchWithAuth<{ success: boolean; data: Invitation }>(`/organizations/${orgId}/invite`, {
      method: "POST",
      body: JSON.stringify(req),
    }),
  listOrgInvitations: (orgId: string) =>
    fetchWithAuth<{ success: boolean; data: Invitation[] }>(`/organizations/${orgId}/invitations`),
  cancelOrgInvitation: (orgId: string, invitationId: string) =>
    fetchWithAuth<void>(`/organizations/${orgId}/invitations/${invitationId}`, {
      method: "DELETE",
    }),

  // Users
  listUsers: async (params?: { page?: number; per_page?: number; org_id?: string }): Promise<PaginatedUsers> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    if (params?.org_id) searchParams.set("org_id", params.org_id);
    return fetchWithAuth<PaginatedUsers>(`/users?${searchParams}`);
  },
  getUser: (id: string) => fetchWithAuth<User>(`/users/${id}`),
  deleteUser: (id: string) =>
    fetchWithAuth<void>(`/users/${id}`, { method: "DELETE" }),

  // Statistics
  getStats: () => fetchWithAuth<SystemStats>("/stats"),

  // Tiers
  listTiers: () => fetchWithAuth<{ tiers: TierConfig[] }>("/tiers"),
  getTier: (tier: string) => fetchWithAuth<TierConfig>(`/tiers/${tier}`),
  updateTier: (tier: string, updates: Partial<TierConfig>) =>
    fetchWithAuth<TierConfig>(`/tiers/${tier}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    }),

  // Organization Overrides
  getOrgOverrides: (orgId: string) => fetchWithAuth<OrgOverrides | null>(`/orgs/${orgId}/overrides`),
  setOrgOverrides: (orgId: string, overrides: Partial<OrgOverrides>) =>
    fetchWithAuth<OrgOverrides>(`/orgs/${orgId}/overrides`, {
      method: "PUT",
      body: JSON.stringify(overrides),
    }),
  deleteOrgOverrides: (orgId: string) =>
    fetchWithAuth<void>(`/orgs/${orgId}/overrides`, { method: "DELETE" }),

  // Cache
  invalidateCache: () =>
    fetchWithAuth<{ message: string }>("/cache/invalidate", { method: "POST" }),

  // Recon
  getReconStats: () => fetchWithAuth<ReconStats>("/recon/stats"),
  getReconConfig: () => fetchWithAuth<ReconConfig>("/recon/config"),
  updateReconConfig: (updates: Partial<ReconConfig>) =>
    fetchWithAuth<ReconConfig>("/recon/config", {
      method: "PUT",
      body: JSON.stringify(updates),
    }),
  enableRecon: () =>
    fetchWithAuth<ReconConfig>("/recon/config/enable", { method: "POST" }),
  disableRecon: () =>
    fetchWithAuth<ReconConfig>("/recon/config/disable", { method: "POST" }),
  listReconDomains: async (params?: {
    page?: number;
    per_page?: number;
    tld?: string;
    country?: string;
    search?: string;
  }): Promise<PaginatedReconDomains> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    if (params?.tld) searchParams.set("tld", params.tld);
    if (params?.country) searchParams.set("country", params.country);
    if (params?.search) searchParams.set("search", params.search);
    return fetchWithAuth<PaginatedReconDomains>(`/recon/domains?${searchParams}`);
  },
  getReconDomain: (id: string) => fetchWithAuth<ReconDomain>(`/recon/domains/${id}`),
  deleteReconDomain: (id: string) =>
    fetchWithAuth<void>(`/recon/domains/${id}`, { method: "DELETE" }),
  purgeReconDomains: () =>
    fetchWithAuth<{ domains_deleted: number; results_deleted: number }>("/recon/domains/purge", {
      method: "POST",
      body: JSON.stringify({ confirmation: "DELETE ALL DOMAINS" }),
    }),
  importReconDomains: (request: ReconImportRequest) =>
    fetchWithAuth<ReconImportResponse>("/recon/domains/import", {
      method: "POST",
      body: JSON.stringify(request),
    }),
  getReconTlds: () => fetchWithAuth<{ tlds: string[] }>("/recon/domains/tlds"),
  getReconCountries: () => fetchWithAuth<{ countries: string[] }>("/recon/domains/countries"),
  getHighSeverityFindings: async (params?: { page?: number; per_page?: number; severity?: string; domain?: string; module?: string; search?: string; campaign_id?: string }): Promise<{ findings: ReconFinding[]; total: number; critical_count: number; high_count: number; page: number; per_page: number }> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    if (params?.severity) searchParams.set("severity", params.severity);
    if (params?.domain) searchParams.set("domain", params.domain);
    if (params?.module) searchParams.set("module", params.module);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.campaign_id) searchParams.set("campaign_id", params.campaign_id);
    return fetchWithAuth<{ findings: ReconFinding[]; total: number; critical_count: number; high_count: number; page: number; per_page: number }>(`/recon/findings/high-severity?${searchParams}`);
  },
  getReconResult: (id: string) => fetchWithAuth<ReconResult>(`/recon/results/${id}`),
  downloadReconResult: async (domainId: string, domainName: string) => {
    const token = useAuthStore.getState().token;
    const response = await fetch(`${API_BASE}/recon/domains/${domainId}/download`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const msg = await response.text().catch(() => response.statusText);
      throw new Error(msg || `Download failed (${response.status})`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `recon_${domainName}.json.gz`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  exportHighSeverityFindings: async (params?: { severity?: string; domain?: string; module?: string; search?: string; campaign_id?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.severity) searchParams.set("severity", params.severity);
    if (params?.domain) searchParams.set("domain", params.domain);
    if (params?.module) searchParams.set("module", params.module);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.campaign_id) searchParams.set("campaign_id", params.campaign_id);
    const token = useAuthStore.getState().token;
    const response = await fetch(`${API_BASE}/recon/findings/high-severity/export?${searchParams}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const msg = await response.text().catch(() => response.statusText);
      throw new Error(msg || `Export failed (${response.status})`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition");
    const filenameMatch = disposition?.match(/filename="(.+?)"/);
    const filename = filenameMatch?.[1] || "recon_findings.json.gz";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // Analytics (Tier 1 - Materialized Views)
  getTldSummary: () => fetchWithAuth<TldSummary[]>("/recon/analytics/tld-summary"),
  getFindingDistribution: () =>
    fetchWithAuth<FindingDistribution[]>("/recon/analytics/finding-distribution"),
  getDailyActivity: () => fetchWithAuth<DailyActivity[]>("/recon/analytics/daily-activity"),
  refreshAnalytics: () =>
    fetchWithAuth<{ message: string; refreshed_at: string }>("/recon/analytics/refresh", {
      method: "POST",
    }),

  // Analytics (Tier 2 - MapReduce Jobs)
  createAnalyticsJob: (params: {
    job_type: AnalyticsJobType;
    date_start: string;
    date_end: string;
  }) =>
    fetchWithAuth<AnalyticsJob>("/recon/analytics/jobs", {
      method: "POST",
      body: JSON.stringify(params),
    }),
  listAnalyticsJobs: () =>
    fetchWithAuth<{ jobs: AnalyticsJob[] }>("/recon/analytics/jobs"),
  getAnalyticsJob: (id: string) =>
    fetchWithAuth<AnalyticsJob>(`/recon/analytics/jobs/${id}`),

  // Campaigns
  createCampaign: (data: CreateCampaignRequest) =>
    fetchWithAuth<ReconCampaign>("/recon/campaigns", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  listCampaigns: async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
    sector?: string;
    country_code?: string;
    search?: string;
  }): Promise<PaginatedCampaigns> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    if (params?.status) searchParams.set("status", params.status);
    if (params?.sector) searchParams.set("sector", params.sector);
    if (params?.country_code) searchParams.set("country_code", params.country_code);
    if (params?.search) searchParams.set("search", params.search);
    return fetchWithAuth<PaginatedCampaigns>(`/recon/campaigns?${searchParams}`);
  },
  getCampaign: (id: string) =>
    fetchWithAuth<ReconCampaign>(`/recon/campaigns/${id}`),
  updateCampaign: (id: string, data: UpdateCampaignRequest) =>
    fetchWithAuth<ReconCampaign>(`/recon/campaigns/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  deleteCampaign: (id: string) =>
    fetchWithAuth<void>(`/recon/campaigns/${id}`, { method: "DELETE" }),
  pauseCampaign: (id: string) =>
    fetchWithAuth<ReconCampaign>(`/recon/campaigns/${id}/pause`, { method: "POST" }),
  resumeCampaign: (id: string) =>
    fetchWithAuth<ReconCampaign>(`/recon/campaigns/${id}/resume`, { method: "POST" }),
  listCampaignDomains: async (id: string, params?: { page?: number; per_page?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<PaginatedReconDomains>(`/recon/campaigns/${id}/domains?${searchParams}`);
  },
  addCampaignDomains: (id: string, domain_ids: string[]) =>
    fetchWithAuth<{ added: number }>(`/recon/campaigns/${id}/domains`, {
      method: "POST",
      body: JSON.stringify({ domain_ids }),
    }),
  removeCampaignDomain: (id: string, domainId: string) =>
    fetchWithAuth<void>(`/recon/campaigns/${id}/domains/${domainId}`, { method: "DELETE" }),
  importCampaignDomains: (id: string, domains: string[], source?: string) =>
    fetchWithAuth<{ imported: number }>(`/recon/campaigns/${id}/import`, {
      method: "POST",
      body: JSON.stringify({ domains, source }),
    }),
  getCampaignStats: (id: string) =>
    fetchWithAuth<CampaignStats>(`/recon/campaigns/${id}/stats`),
  getCampaignFindings: (id: string, params?: { page?: number; per_page?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<{ findings: CampaignFinding[]; total: number; page: number; per_page: number }>(`/recon/campaigns/${id}/findings?${searchParams}`);
  },
  exportCampaignCsv: async (id: string, campaignName: string) => {
    const token = useAuthStore.getState().token;
    const response = await fetch(`${API_BASE}/recon/campaigns/${id}/export/csv`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const msg = await response.text().catch(() => response.statusText);
      throw new Error(msg || `CSV export failed (${response.status})`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition");
    const filenameMatch = disposition?.match(/filename="(.+?)"/);
    const filename = filenameMatch?.[1] || `campaign_${campaignName}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  listCampaignResults: async (id: string, params?: { page?: number; per_page?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<{ results: CampaignResult[]; total: number; page: number; per_page: number }>(
      `/recon/campaigns/${id}/results?${searchParams}`
    );
  },
};
